import { useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { CheckCircle2, Clock, LogOut, Mail, Send, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import {
  useCreateAccessRequest,
  useMyAccessRequest,
} from '@/hooks/useAccessRequest';
import { teamLabel } from '@/lib/labels';
import { formatDate } from '@/lib/dates';

/**
 * Static on purpose: someone waiting for approval can't read the admin teams
 * API. Keep in step with the teams set up under Setup → Teams.
 */
const TEAM_OPTIONS = ['SOCIAL_MEDIA', 'DISCORD', 'VYRAL', 'INFLUENCER'].map((code) => ({
  value: code,
  label: teamLabel(code),
}));

const NOTE_MIN = 10;
const NOTE_MAX = 1000;

export function ClaimPending(): JSX.Element {
  const { user } = useUser();
  const { signOut } = useClerk();
  const email = user?.primaryEmailAddress?.emailAddress;

  const myRequest = useMyAccessRequest();
  const createRequest = useCreateAccessRequest();

  // No default: people kept landing on the wrong team by leaving it as-is.
  const [team, setTeam] = useState('');
  const [note, setNote] = useState('');

  const existing = myRequest.data ?? null;
  const noteValid = note.trim().length >= NOTE_MIN && note.trim().length <= NOTE_MAX;
  const canSubmit = noteValid && !!team && !createRequest.isPending;

  async function submit(): Promise<void> {
    if (!team) {
      toast.error('Pick the team you are joining.');
      return;
    }
    try {
      await createRequest.mutateAsync({
        requestedTeam: team,
        note: note.trim(),
      });
      setNote('');
      toast.success('Request sent. An admin will review it.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 py-12">
      <Card>
        <CardHeader>
          <CardTitle>Account pending approval</CardTitle>
          <CardDescription>
            <span className="font-mono">{email ?? 'Your account'}</span> isn't connected to an
            outreacher yet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Send a request below and an admin will set you up, or ask an admin to add you
            directly.
          </p>
          <p className="flex items-center gap-2 text-foreground">
            <Mail className="h-4 w-4" /> Once an admin confirms, refresh this page.
          </p>
          <div className="flex justify-end border-t pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => signOut({ redirectUrl: '/sign-in' })}
            >
              <LogOut className="mr-1 h-3 w-3" /> Sign out
            </Button>
          </div>
        </CardContent>
      </Card>

      {existing && existing.status === 'PENDING' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-4 w-4 text-pending-strong" /> Awaiting approval
            </CardTitle>
            <CardDescription>
              Sent {formatDate(existing.createdAt)} to join{' '}
              {existing.requestedTeam ? `the ${teamLabel(existing.requestedTeam)} team` : 'any team'}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="rounded-md border bg-muted/30 p-3 whitespace-pre-wrap text-foreground/90">
              {existing.note}
            </div>
            <p className="text-xs text-muted-foreground">
              An admin will review this and either set you up or get back to you. You'll
              land in the portal automatically once approved.
            </p>
          </CardContent>
        </Card>
      )}

      {existing && existing.status === 'REJECTED' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <XCircle className="h-4 w-4 text-destructive" /> Request declined
            </CardTitle>
            <CardDescription>
              Reviewed {formatDate(existing.reviewedAt ?? existing.updatedAt)}
              {existing.rejectionReason ? ' — see reason below.' : '.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {existing.rejectionReason && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-foreground/90">
                {existing.rejectionReason}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Reach out to your admin contact directly if you think this was a mistake.
              You can also send a new request below.
            </p>
          </CardContent>
        </Card>
      )}

      {existing && existing.status === 'APPROVED' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4 text-success-strong" /> Approved
            </CardTitle>
            <CardDescription>
              Refresh this page to land in your portal.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {(!existing || existing.status !== 'PENDING') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Request access</CardTitle>
            <CardDescription>
              Tell us who you are and which team you're joining. Admin reviews are async —
              usually within a day.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-1.5">
              <Label>Team</Label>
              <Combobox
                value={team}
                onChange={setTeam}
                options={TEAM_OPTIONS}
                placeholder="Pick your team"
              />
              <p className="text-xs text-muted-foreground">
                Required. An admin confirms the team when they approve you.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="note">Note for the admin</Label>
              <textarea
                id="note"
                className="min-h-[100px] w-full rounded-md border bg-transparent px-3 py-2 text-sm"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Quick intro — who referred you, which team you'll be on, etc."
                maxLength={NOTE_MAX}
              />
              <p className="text-right text-[10px] text-muted-foreground">
                {note.trim().length} / {NOTE_MAX} (min {NOTE_MIN})
              </p>
            </div>
            <div className="flex justify-end">
              <Button onClick={submit} disabled={!canSubmit}>
                <Send className="mr-1 h-4 w-4" /> Send request
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
